/** Shared 2D motion curves — knock arcs, leap travel ease (Phaser-free). */

export function clamp01(t: number): number {
  return Math.min(1, Math.max(0, t));
}

/** Fast launch → slow settle. */
export function easeOutCubic(t: number): number {
  const u = 1 - clamp01(t);
  return 1 - u * u * u;
}

/** Smooth accelerate then decelerate (boss leap travel XY). */
export function easeInOutCubic(t: number): number {
  const x = clamp01(t);
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
}

/** Symmetric bump peaking at 1 when t=0.5. */
export function parabola01(t: number): number {
  const x = clamp01(t);
  return 4 * x * (1 - x);
}

export type Pt2 = { x: number; y: number };

export type KnockFlightSample = {
  x: number;
  y: number;
  /** Screen-space lift (subtract from draw Y). */
  height: number;
};

/**
 * Ground XY: non-uniform but finishes by ~settleAt (no long crawl).
 * Height: full-duration parabola hang.
 */
export function sampleKnockFlight(
  t: number,
  from: Pt2,
  to: Pt2,
  apex: number,
  settleAt = 0.62,
): KnockFlightSample {
  const u = clamp01(t);
  const settle = Math.min(0.95, Math.max(0.35, settleAt));
  // Remap: complete easeOut by settleAt, then hold — avoids easeOut tail crawl.
  const g = u >= settle ? 1 : easeOutCubic(u / settle);
  return {
    x: from.x + (to.x - from.x) * g,
    y: from.y + (to.y - from.y) * g,
    height: parabola01(u) * apex,
  };
}

/** Fake camera depth while a body is screen-lifted (knock / leap travel). */
export type AirborneDepthOpts = {
  /** Extra body scale at apex (1 + boost). */
  maxScaleBoost?: number;
  /** Soft shadow size floor at apex (1 = no shrink). */
  shadowMinScale?: number;
  /** Soft shadow alpha floor at apex. */
  shadowMinAlpha?: number;
};

export type AirborneDepthSample = {
  /** 0 ground → 1 apex. */
  lift01: number;
  /** Apply to resting body scale. */
  bodyScale: number;
  /** Multiply resting shadow ellipse size. */
  shadowScale: number;
  /** Multiply resting shadow alpha. */
  shadowAlphaMul: number;
};

/**
 * Perspective cheat: body grows toward camera at apex; ground shadow shrinks/fades.
 * Height is screen-space lift (same units as `KnockFlightSample.height`).
 */
export function sampleAirborneDepth(
  height: number,
  apex: number,
  opts?: AirborneDepthOpts,
): AirborneDepthSample {
  const ap = Math.max(0, apex);
  const lift01 = ap > 1e-6 ? clamp01(height / ap) : 0;
  const boost = Math.max(0, opts?.maxScaleBoost ?? 0.42);
  const shMin = clamp01(opts?.shadowMinScale ?? 0.42);
  const aMin = clamp01(opts?.shadowMinAlpha ?? 0.38);
  return {
    lift01,
    bodyScale: 1 + boost * lift01,
    shadowScale: 1 - (1 - shMin) * lift01,
    shadowAlphaMul: 1 - (1 - aMin) * lift01,
  };
}

/** Slight body tilt along ground flight direction (radians). */
export function airborneTiltRad(
  from: Pt2,
  to: Pt2,
  lift01: number,
  maxTiltRad: number,
): number {
  const dx = to.x - from.x;
  const sign = dx === 0 ? 1 : Math.sign(dx);
  return sign * Math.max(0, maxTiltRad) * lift01;
}
